import type { AssumptionRecord, WorldDraft, WizardStepId } from '../../domain/types.js';
import {
  setLocationGrounding,
  setSchedule,
  setTimePeriod,
} from './draftReducer.js';
import { defaultSchedule, suggestGroundingField, suggestTimePeriod } from './fallbacks.js';
import { emptyLocationGrounding } from './questionSchema.js';

export interface FieldRegenResult {
  draft: WorldDraft;
  status: string;
  step: WizardStepId;
}

export function regenerateFieldAssumption(draft: WorldDraft, fieldPath: string): FieldRegenResult | null {
  if (fieldPath === 'location' || fieldPath.startsWith('location')) {
    const g = {
      name: String(suggestGroundingField(draft.draftId, 'name').value),
      region: String(suggestGroundingField(draft.draftId, 'region').value),
      language: String(suggestGroundingField(draft.draftId, 'language').value),
    };
    return {
      draft: setLocationGrounding(draft, { ...emptyLocationGrounding(), ...g }, { source: 'fallback' }),
      status: 'Updated location from local suggestion.',
      step: 'location-grounding',
    };
  }
  if (fieldPath === 'timePeriod' || fieldPath.startsWith('timePeriod')) {
    const period = suggestTimePeriod(draft.draftId);
    return {
      draft: setTimePeriod(draft, period),
      status: `Regenerated period: ${period.present ?? period.period ?? 'Present day'}`,
      step: 'time-period',
    };
  }
  if (fieldPath === 'schedule' || fieldPath.startsWith('schedule')) {
    const sched = defaultSchedule(null, draft.draftId);
    return {
      draft: setSchedule(draft, sched),
      status: `Regenerated schedule: ${sched.hasOperatingHours ? `${sched.openTime}–${sched.closeTime}` : 'Live 24/7'}`,
      step: 'operating-hours',
    };
  }
  return null;
}

export function acceptReviewAnswerDraft(draft: WorldDraft, questionId: string): WorldDraft {
  const a = draft.answers[questionId];
  if (!a || a.status === 'unanswered' || a.status === 'skipped') return draft;
  const now = new Date().toISOString();
  return {
    ...draft,
    answers: {
      ...draft.answers,
      [questionId]: {
        ...a,
        status: 'confirmed',
        source: a.source ?? 'user',
        updatedAt: now,
      },
    },
  };
}

export function assumptionChangeStep(assumption: AssumptionRecord): WizardStepId {
  if (assumption.fieldPath.startsWith('timePeriod')) return 'time-period';
  if (assumption.fieldPath.startsWith('schedule')) return 'operating-hours';
  if (assumption.fieldPath.startsWith('connected')) return 'connected';
  return 'location-grounding';
}
